/**
 * Worklist's view model: a port of the design's Worklist logic (Aithora ConsoleV3.dc.html
 * L36050-36533, L37643-38330, the action layer wlLayer L34453) over the live routes.
 * Pure: the page passes the API answers, the lens and the clock; this returns every value
 * the template draws. Nothing here invents a figure: where a route does not carry a field
 * yet (PROPOSED in @aithora/shared), the row says what is missing instead.
 */
import type {
  AtRiskResponse,
  BenefitItem,
  CareRequest,
  FeeScheduleCode,
  MedPassRow,
  NutritionRow,
  PartANoteRow,
  Referral,
  WorklistItem,
} from '@aithora/shared';

export type Lens = 'staff' | 'admin' | 'ro';
export type TabKey = 'measures' | 'notes' | 'requests' | 'medpass' | 'nutrition' | 'benefits' | 'referrals';
export type ViewKey = 'All' | 'At risk' | 'Handed off' | 'Selected';

export const TAB_LABEL: Record<TabKey, string> = {
  measures: 'Measures', notes: 'Part A notes', requests: 'Requests', medpass: 'Med pass', nutrition: 'Nutrition', benefits: 'Benefits', referrals: 'Referrals',
};

const M = 60_000;
const H = 3_600_000;

/** The design's wqFmt: "3h 07m", "4m 00s", "2d 3h". */
export function wqFmt(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600);
  const m = Math.floor(t / 60) % 60;
  const sec = t % 60;
  const d = Math.floor(h / 24);
  if (d >= 1) return `${d}d ${h % 24}h`;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(sec).padStart(2, '0')}s`;
}

/**
 * A due time as the design's one-second ticker writes it into every [data-wq-due]
 * (L34149): under an hour it counts seconds, "25m 00s overdue", "due in 40m 00s".
 */
export function dueFmt(ms: number): string {
  const a = Math.abs(ms);
  const m = Math.round(a / M);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  const sec = Math.floor(a / 1000) % 60;
  const t = d >= 1 ? `${d}d ${h % 24}h` : h >= 1 ? `${h}h ${m % 60}m` : `${Math.floor(a / M)}m ${String(sec).padStart(2, '0')}s`;
  return ms < 0 ? `${t} overdue` : `due in ${t}`;
}

/** Whole minutes as the chips write them: "45m", "8h 58m", "2d 3h". */
export function minsText(mins: number): string {
  const m = Math.round(mins);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return `${h}h ${m % 60}m`;
}

/** The design's clock(): "02:12 PM". */
export const clockText = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// ── Tabs (Priority+, design L37648) ─────────────────────────────────────────

export interface TabCount {
  k: TabKey;
  label: string;
  n: number;
}

/**
 * The tab row: the role's anchored tab first, then pinned tabs, then the rest by how much
 * is outstanding, declaration order breaking ties so two zeroes never swap. Selecting a
 * tab never reorders the row. Care Staff land on Requests and have no Referrals.
 */
export function orderTabs(lens: Lens, counts: Record<TabKey, number>, pinned: TabKey[] = []): TabCount[] {
  // V9 (design wTabs): Care Staff have two tabs, "Your tasks" (their requests and their own
  // scheduled tasks) and Med pass; everyone else has the full row.
  const all: TabKey[] = lens === 'staff' ? ['requests', 'medpass'] : ['measures', 'notes', 'requests', 'medpass', 'nutrition', 'benefits'];
  if (lens !== 'staff') all.push('referrals');
  const anchored: TabKey = lens === 'staff' ? 'requests' : 'measures';
  const idx = new Map(all.map((k, i) => [k, i]));
  const rank = (k: TabKey) => (k === anchored ? -1500 : 0) + (pinned.includes(k) ? -500 : 0) - counts[k];
  return all
    .slice()
    .sort((p, q) => rank(p) - rank(q) || idx.get(p)! - idx.get(q)!)
    .map((k) => ({ k, label: lens === 'staff' && k === 'requests' ? 'Your tasks' : TAB_LABEL[k], n: counts[k] }));
}

export const homeTab = (lens: Lens): TabKey => (lens === 'staff' ? 'requests' : 'measures');

// ── The action layer (design wlLayer mk()) ──────────────────────────────────

/** An item's exit as the page knows it: from the server's fold for tasks and requests. */
export interface Exit {
  st: 'done' | 'snz' | 'hand' | 'ref';
  at: number;
  by: string;
  why?: string;
  to?: string;
  until?: number;
  snzN: number;
}

export interface AxInput {
  tab: TabKey;
  /** `${tab}:${sourceId}`, the key the at-risk list and selection use. */
  id: string;
  who: string;
  label: string;
  dueMs: number | null;
  hasDone: boolean;
  full?: boolean;
  exit: Exit | null;
}

export interface Ax {
  show: boolean;
  id: string;
  tab: TabKey;
  who: string;
  label: string;
  mins: number | null;
  /** The row shows its exit buttons (not handed off, not closed). */
  open: boolean;
  /** Still on someone's plate (design it.open): not done, refused or snoozed; a handed-off item is live. */
  live: boolean;
  snzOn: boolean;
  hasDone: boolean;
  wrap: string;
  selected: boolean;
  selMark: string;
  selStyle: string;
  doneStyle: string;
  btnStyle: string;
  tipD: string;
  tipS: string;
  tipH: string;
  tipR: string;
  hasState: boolean;
  stateText: string;
  stateStyle: string;
  canUndo: boolean;
  risk: boolean;
  riskWhy: string;
  score: number;
  exit: Exit | null;
}

export const SNOOZES: [string, number, 'asleep' | 'out_of_building' | 'declined'][] = [
  ['Asleep · 1h', 1, 'asleep'],
  ['Out of the building · 4h', 4, 'out_of_building'],
  ['Declined · next shift', 8, 'declined'],
];
/** Hand-off targets: the charge nurse and any CNA are on-call roles; "Next shift" goes to whoever is scheduled next (server). */
export const HANDS: [string, string][] = [
  ['Charge nurse', 'CHARGE_NURSE'],
  ['Any CNA', 'AIDE'],
  ['Next shift', 'NEXT_SHIFT'],
];
export const REFUSALS = ['Resident refused', 'Not appropriate today', 'Family asked to wait'];

export function makeAx(inp: AxInput, o: { ro: boolean; phone: boolean; roleName: string; now: number; selected: boolean }): Ax {
  const a = inp.exit;
  const snzOn = !!(a && a.st === 'snz' && (a.until ?? 0) > o.now);
  const open = !a || (a.st === 'snz' && !snzOn) || a.st === 'hand';
  const btn =
    'display:inline-flex;align-items:center;justify-content:center;min-height:' + (o.phone ? 44 : 34) + 'px;padding:0 ' + (o.phone ? 6 : 11) + 'px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' + (o.phone ? 'flex:1 1 0;min-width:0;' : '');
  const g = o.ro ? 'color:#3C454F;background:rgba(34,39,46,.04);border:1px dashed rgba(34,39,46,.38);cursor:not-allowed;' : 'color:#22272E;background:#FCFAF4;border:1px solid rgba(34,39,46,.22);';
  const stateText = !a
    ? ''
    : a.st === 'done'
      ? `✓ Done ${clockText(a.at)} by ${a.by}`
      : a.st === 'ref'
        ? `Refused · ${a.why ?? ''} · ${clockText(a.at)}`
        : a.st === 'hand'
          ? `Handed to ${a.to ?? ''} · ${clockText(a.at)}`
          : a.st === 'snz' && !snzOn
            ? `Back from snooze (${a.why ?? ''})`
            : '';
  const ro = o.ro;
  return {
    // V5: a read-only role (Super Admin, Operator, Partner) renders no row actions, only a recorded state.
    show: !ro || !!stateText,
    id: inp.id,
    tab: inp.tab,
    who: inp.who,
    label: inp.label,
    mins: inp.dueMs ? Math.round((inp.dueMs - o.now) / M) : null,
    open: !ro && open && !(a && a.st === 'hand'),
    live: open,
    snzOn,
    hasDone: inp.hasDone,
    wrap: o.phone
      ? 'flex:1 1 100%;grid-column:1/-1;display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0;padding:4px 0 2px;'
      : inp.full
        ? 'flex:1 1 100%;grid-column:1/-1;display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:flex-end;min-width:0;padding:0 4px 8px;'
        : 'flex:0 0 auto;grid-column:1/-1;margin-left:auto;display:flex;align-items:center;gap:6px;flex-wrap:nowrap;justify-content:flex-end;min-width:0;',
    selected: o.selected,
    selMark: o.selected ? '✓' : '',
    selStyle:
      'display:inline-flex;align-items:center;justify-content:center;' + (o.phone ? 'flex:0 0 44px;width:44px;height:44px;border-radius:9px;font-size:16px;' : 'width:22px;height:25px;border-radius:6px;font-size:16px;') +
      'font-weight:800;text-decoration:none;' + (ro ? 'display:none;' : o.selected ? 'background:#2F5C97;color:#fff;border:1.5px solid #2F5C97;' : 'border:1.5px solid rgba(34,39,46,.3);color:#2F5C97;'),
    doneStyle: btn + (ro ? 'color:#3C454F;background:rgba(34,39,46,.04);border:1px dashed rgba(34,39,46,.38);cursor:not-allowed;' : 'color:#fff;background:#1D3F6B;border:1px solid #1D3F6B;'),
    btnStyle: btn + g,
    tipD: ro ? `Read-only as ${o.roleName}` : 'Done (D)',
    tipS: ro ? `Read-only as ${o.roleName}` : 'Snooze (S)',
    tipH: ro ? `Read-only as ${o.roleName}` : 'Hand off (H)',
    tipR: ro ? `Read-only as ${o.roleName}` : 'Refused (R) · the reason is recorded',
    hasState: !!stateText,
    stateText,
    stateStyle: 'font-size:16px;font-weight:700;white-space:nowrap;color:' + (a && a.st === 'done' ? '#1F6B52' : a && a.st === 'ref' ? '#B3261E' : '#3C454F') + ';',
    canUndo: !!a && !ro,
    risk: false,
    riskWhy: '',
    score: 0,
    exit: a,
  };
}

/** The exit a worklist item shows, from the server's fold (status + last action). */
export function exitOfItem(i: WorklistItem, meId: string | null): Exit | null {
  const la = i.lastAction;
  if (!la || la.action === 'UNDO') return null;
  const by = la.actorName ?? 'Staff';
  const at = Date.parse(la.at);
  if (i.status === 'done' && la.action === 'DONE') return { st: 'done', at, by, snzN: i.snoozeCount };
  if (i.status === 'refused') return { st: 'ref', at, by, why: la.reason ?? '', snzN: i.snoozeCount };
  if (la.action === 'SNOOZE') return { st: 'snz', at, by, why: la.reason ?? '', until: i.snoozeUntil ? Date.parse(i.snoozeUntil) : at, snzN: i.snoozeCount };
  if (la.action === 'HANDOFF') return { st: 'hand', at, by, to: i.ownerName ?? roleLabel(i.ownerRole) ?? (i.ownerId === meId ? 'you' : 'someone'), snzN: i.snoozeCount };
  return null;
}

/** An owner role in words (OWNER_ROLES in v3/worklist/access.ts). */
export function roleLabel(role: string | null | undefined): string | null {
  if (!role) return null;
  return (
    ({ CAREGIVER: 'Assigned to floor staff', STAFF: 'Assigned to floor staff', AIDE: 'Any CNA', CHARGE_NURSE: 'Charge nurse', ADMIN: 'Facility Admin', ADMIN_ON_DUTY: 'Administrator on duty' } as Record<string, string>)[role] ??
    role.toLowerCase().replace(/_/g, ' ')
  );
}

/** The at-risk list (GET /worklist/at-risk, MODELED), keyed the way rows are. */
export function riskIndex(r: AtRiskResponse | null): Map<string, { why: string; score: number; who: string; label: string; tab: TabKey }> {
  const out = new Map<string, { why: string; score: number; who: string; label: string; tab: TabKey }>();
  if (!r) return out;
  for (const it of r.items) {
    const tab = (it.itemRef.tab === ('tasks' as string) ? 'measures' : (it.itemRef.tab as string)) as TabKey;
    const val = (k: string) => it.reasons.find((x) => x.key === k)?.value;
    const left = val('time_left');
    const ahead = val('items_ahead');
    const snz = Number(val('snooze_count') ?? 0);
    const parts: string[] = [];
    // Minutes from the server, written as hours and minutes past an hour ("due in 8h 58m", not "538m").
    if (typeof left === 'number') parts.push(left < 0 ? `${minsText(Math.abs(left))} overdue` : `due in ${minsText(left)}`);
    if (typeof ahead === 'number' && ahead) parts.push(`${ahead} ahead of it`);
    if (snz >= 2) parts.push(`snoozed ${snz}× → charge nurse`);
    if (it.reasons.some((x) => x.key === 'owner')) parts.push('nobody owns it');
    // handed on but still open: who has it now (design wlLayer "with Charge nurse")
    const handed = it.reasons.find((x) => x.key === 'handed_to');
    if (handed) parts.push(`with ${handed.text}`);
    out.set(`${tab}:${it.itemRef.sourceId}`, {
      why: `${TAB_LABEL[tab]} · ${it.task} · ${parts.join(' · ')}`,
      score: (typeof left === 'number' ? left : 60) - (snz >= 2 ? 120 : 0),
      who: it.residentName ?? 'Resident',
      label: it.task,
      tab,
    });
  }
  return out;
}

// ── Measures (design wq*, L37923) ───────────────────────────────────────────

export interface CodeInfo {
  label: string;
  rate: string | null;
  status: 'VERIFIED' | 'ESTIMATED' | null;
}

/** "$66.13/mo" from cents and the rate unit. */
export function rateText(cents: number | null, unit: string | null | undefined): string | null {
  if (cents == null) return null;
  return `$${(cents / 100).toFixed(2)}${unit ? `/${unit}` : ''}`;
}

/**
 * Does a code's billing period ('mo', '6mo', 'yr') end today? (design wDaysLeftIn(): the
 * days left in the month, the half or the year, zero on its last day). Per-occasion codes
 * (null) have no period.
 */
export function periodClosesToday(unit: string | null, now: number): boolean {
  const d = new Date(now);
  const y = d.getFullYear();
  const m = d.getMonth();
  const last = unit === 'mo' ? new Date(y, m + 1, 0) : unit === '6mo' ? new Date(y, m < 6 ? 6 : 12, 0) : unit === 'yr' ? new Date(y, 11, 31) : null;
  return !!last && last.getDate() === d.getDate() && last.getMonth() === m;
}

export function codeIndex(codes: FeeScheduleCode[]): Map<string, CodeInfo> {
  const out = new Map<string, CodeInfo>();
  for (const c of codes) {
    if (c.code == null) continue; // a masked code has no identity to index by
    out.set(c.code, { label: c.name ?? c.code, rate: rateText(c.rateCents, c.rateUnit), status: c.rateCents == null ? null : c.rateVerified ? 'VERIFIED' : 'ESTIMATED' });
  }
  return out;
}

export interface MeasureRow {
  id: string;
  item: WorklistItem;
  res: string;
  room: string;
  task: string;
  track: string;
  trackStyle: string;
  unlock: string;
  badge: string;
  badgeStyle: string;
  owner: string;
  ownerColor: string;
  dueAt: number;
  dueText: string;
  dueColor: string;
  cap: 'number' | 'score' | 'yesno';
  ph: string;
  isDone: boolean;
  doneText: string;
  rowStyle: string;
  ms: number;
}

export function measureRow(i: WorklistItem, o: { now: number; meId: string | null; ro: boolean; codes: Map<string, CodeInfo>; justDone?: string | null }): MeasureRow {
  const due = Date.parse(i.dueAt);
  const ms = due - o.now;
  const r = i.code ? o.codes.get(i.code) : undefined;
  const status = i.rateStatus ?? r?.status ?? null;
  const rate = r?.rate ?? rateText(i.rateCents, null);
  const est = status === 'ESTIMATED';
  const isDone = !!o.justDone;
  const mine = !!o.meId && i.ownerId === o.meId;
  const ownerName = i.ownerName ?? roleLabel(i.ownerRole);
  return {
    id: i.id,
    item: i,
    res: i.residentName ?? 'Facility-wide',
    room: i.room ? `Rm ${i.room}` : '',
    task: i.task,
    track: i.track ?? '',
    trackStyle: 'font-size:14px;font-weight:700;letter-spacing:.06em;color:' + (i.track === 'DRIVE' ? '#264C7E' : '#54606C') + ';',
    unlock:
      i.source === 'custom' && !i.code
        ? 'Custom item · no billing code'
        : i.code
          ? `Unlocks ${r?.label ?? i.code} · ${i.code}${rate ? ` · ${rate}` : ''}`
          : 'Care and quality · no billing code',
    badge: i.code && status ? status : '',
    badgeStyle: 'font-size:14px;font-weight:700;letter-spacing:.05em;color:' + (est ? '#3C454F' : '#1F6B52') + ';',
    owner: mine ? (o.ro ? 'Assigned to floor staff' : 'Yours') : i.isUnowned ? 'Nobody owns this' : (ownerName ?? 'Owned'),
    ownerColor: i.isUnowned ? '#B3261E' : '#54606C',
    dueAt: due,
    dueText: isDone ? '' : dueFmt(ms),
    dueColor: ms < 0 ? '#B3261E' : '#3C454F',
    cap: i.captureType,
    ph: i.unit ?? '',
    isDone,
    doneText: o.justDone ?? '',
    rowStyle:
      'display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;padding:12px 4px;border-top:1px solid rgba(34,39,46,.10);min-width:0;transition:background .3s;' +
      (isDone ? 'background:rgba(31,107,82,.08);' : ms < 0 ? 'box-shadow:inset 3px 0 0 #B3261E;' : ''),
    ms,
  };
}

export interface MeasureGroup {
  label: string;
  tone: string;
  n: string;
  rows: MeasureRow[];
}

export function measureGroups(rows: MeasureRow[], byResident: boolean): MeasureGroup[] {
  const tone = (ms: number) => (ms < 0 ? '#B3261E' : '#3C454F');
  const sorted = rows.slice().sort((p, q) => p.ms - q.ms);
  if (byResident) {
    const order: string[] = [];
    const by = new Map<string, MeasureRow[]>();
    for (const r of sorted) {
      if (!by.has(r.res)) {
        by.set(r.res, []);
        order.push(r.res);
      }
      by.get(r.res)!.push(r);
    }
    return order.map((name) => {
      const rs = by.get(name)!;
      return { label: name + (rs[0].room ? ` · ${rs[0].room}` : ''), tone: tone(rs[0].ms), n: `${rs.length}${rs.length === 1 ? ' item' : ' items'}`, rows: rs };
    });
  }
  const G: [string, (ms: number) => boolean, string][] = [
    ['Overdue', (ms) => ms < 0, '#B3261E'],
    ['Closing today', (ms) => ms >= 0 && ms < 12 * H, '#3C454F'],
    ['This week', (ms) => ms >= 12 * H && ms < 7 * 24 * H, '#3C454F'],
    ['Later', (ms) => ms >= 7 * 24 * H, '#54606C'],
  ];
  return G.map(([label, f, t]) => {
    const rs = sorted.filter((r) => f(r.ms));
    return { label, tone: t, n: String(rs.length), rows: rs };
  }).filter((g) => g.rows.length);
}

// ── Part A notes (design wNote*, L38088) ────────────────────────────────────

export interface NoteRow {
  key: string;
  row: PartANoteRow;
  name: string;
  room: string;
  missing: string;
  missingStyle: string;
  barTrack: string;
  bar: string;
  rowStyle: string;
  open: boolean;
  dueAt: number;
  clock: string;
  clockColor: string;
  btnLabel: string;
  btnStyle: string;
  hrs: number;
  late: boolean;
  soon: boolean;
  signed: boolean;
}

export function noteRows(rows: PartANoteRow[], now: number): NoteRow[] {
  return rows
    .map((x) => {
      const last = x.lastSignedAt ? Date.parse(x.lastSignedAt) : now - 24 * H;
      const hrs = Math.max(0, (now - last) / H);
      return { x, hrs, signed: !!x.signedNow, late: hrs >= 24, soon: hrs < 24 && hrs >= 20 };
    })
    .sort((p, q) => q.hrs - p.hrs)
    .map(({ x, hrs, signed, late, soon }, i) => {
      const tone = signed ? '#1F6B52' : late ? '#B3261E' : soon ? '#3C454F' : '#54606C';
      const missing = x.missing.length ? `Missing: ${x.missing.join(' and ')}` : 'Inside the window';
      const dueAt = (x.lastSignedAt ? Date.parse(x.lastSignedAt) : now) + 24 * H;
      const ms = dueAt - now;
      return {
        key: x.residentId,
        row: x,
        name: x.name ?? 'Resident',
        room: x.room ? `Room ${x.room}` : (x.facilityName ?? ''),
        missing: signed ? '' : missing,
        missingStyle: 'font-size:14px;margin-top:2px;line-height:1.4;color:' + (x.missing.length && !signed ? '#3C454F' : '#54606C') + ';',
        barTrack: 'display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:5px;',
        bar:
          'display:block;height:100%;border-radius:2px;transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + Math.min(320, i * 30) + 'ms;width:' +
          Math.min(100, Math.round((hrs / 24) * 100)) + '%;background:' + tone + ';',
        rowStyle: 'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:12px 4px;border-top:1px solid rgba(34,39,46,.10);min-width:0;' + (signed ? 'opacity:.62;' : late ? 'box-shadow:inset 3px 0 0 #B3261E;' : ''),
        open: !signed,
        dueAt,
        clock: ms < 0 ? `${wqFmt(-ms)} past 24h` : `${wqFmt(ms)} left`,
        clockColor: late ? '#B3261E' : '#3C454F',
        btnLabel: signed ? 'Signed' : 'Draft and sign',
        btnStyle:
          'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 16px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' +
          (signed ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;' : late ? 'background:#B3261E;color:#fff;' : 'background:#2F5C97;color:#fff;'),
        hrs,
        late,
        soon,
        signed,
      };
    });
}

// ── Requests (design wReq*, L36183) ─────────────────────────────────────────

export const SOURCE_LABEL: Record<string, string> = { call_button: 'Call button', companion: 'Atlas', family_concierge: 'Family concierge' };

export interface RequestRow {
  req: CareRequest;
  id: string;
  since: number;
  isUrg: boolean;
  isOpen: boolean;
  clock: string;
  waitColor: string;
  rowStyle: string;
  name: string;
  room: string;
  kind: string;
  quote: string;
  urg: string;
  src: string;
  srcStyle: string;
  urgStyle: string;
  st: string;
  stStyle: string;
  showAck: boolean;
  showDone: boolean;
  age: string;
}

const chip = (c: string, bg: string) => 'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:4px;padding:2px 7px;color:' + c + ';background:' + bg + ';';

/** "4m ago", "3h ago" (the design's age). */
export function ageText(ms: number): string {
  const m = Math.max(0, Math.round(ms / M));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * A care request's status as the Worklist reads it. The writers do not agree on the
 * word for a met need: the live complete route writes 'DONE', the seed and older
 * writers 'COMPLETED'; a withdrawn suggestion is 'CANCELLED'. Every closed word reads
 * as DONE so a met or withdrawn request is never shown, counted or acknowledged as open.
 */
export function requestStatus(status: string | null | undefined): 'OPEN' | 'ACKNOWLEDGED' | 'DONE' {
  const s = String(status ?? '').toUpperCase();
  if (s === 'DONE' || s === 'COMPLETED' || s === 'CANCELLED') return 'DONE';
  return s === 'ACKNOWLEDGED' ? 'ACKNOWLEDGED' : 'OPEN';
}

export function requestRow(q: CareRequest, now: number): RequestRow {
  const st = requestStatus(q.status);
  const urg = q.urgency === 'URGENT';
  const since = Date.parse(q.created_at);
  const src = q.source ? SOURCE_LABEL[q.source] ?? q.source : 'Source not recorded';
  return {
    req: q,
    id: q.id,
    since,
    isUrg: urg,
    isOpen: st !== 'DONE',
    clock: `Waiting ${wqFmt(now - since)}`,
    waitColor: urg ? '#B3261E' : '#3C454F',
    rowStyle: 'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:12px 4px;border-top:1px solid rgba(34,39,46,.10);min-width:0;' + (st === 'DONE' ? 'opacity:.6;' : urg ? 'box-shadow:inset 3px 0 0 #B3261E;' : ''),
    name: q.residentName ?? 'Resident',
    room: q.room ? `Room ${q.room}` : (q.facilityName ?? ''),
    kind: q.type,
    quote: `“${q.description ?? q.title}”`,
    urg: q.urgency,
    src,
    srcStyle:
      'display:inline-flex;align-items:center;height:16px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;white-space:nowrap;' +
      (src === 'Family concierge' ? 'color:#2F5C97;background:rgba(79,138,214,.12);' : src === 'Atlas' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#3C454F;background:rgba(34,39,46,.07);'),
    urgStyle: chip(urg ? '#B3261E' : '#3C454F', urg ? 'rgba(179,38,30,.13)' : 'rgba(34,39,46,.13)'),
    st,
    stStyle: chip(
      st === 'DONE' ? '#1F6B52' : st === 'ACKNOWLEDGED' ? '#2F5C97' : '#54606C',
      st === 'DONE' ? 'rgba(47,138,111,.13)' : st === 'ACKNOWLEDGED' ? 'rgba(47,92,151,.11)' : 'rgba(34,39,46,.06)',
    ),
    showAck: st === 'OPEN',
    showDone: st !== 'DONE',
    age: ageText(now - since),
  };
}

// ── Med pass (design wMed*, L38205) ─────────────────────────────────────────

export interface MedRow {
  med: MedPassRow;
  key: string;
  /** As needed (PRN): given when the resident needs it, never a dose still to give. */
  prn: boolean;
  /** How the order is given (MedServiceLevel): Assistance offers the Assisted buttons; a
   *  self-administered order is never a dose still to give, only an observation note. */
  level: 'administer' | 'assistance' | 'self_administered';
  res: string;
  room: string;
  name: string;
  dose: string;
  due: number | null;
  recorded: boolean;
  st: string;
  stStyle: string;
  dueText: string;
  dueColor: string;
  rowStyle: string;
}

/** A PRN (as needed) order, from its frequency text. */
export const isPrn = (frequency: string | null | undefined): boolean => /\bPRN\b|as needed/i.test(frequency ?? '');

/** Today's record in plain words: "GIVEN", "ASSISTED · TAKEN", "ASSISTED · REFUSED", "NOTED". */
export function medRecordLabel(status: MedPassRow['today_status'], assisted: boolean | undefined): string {
  if (!status) return '';
  if (status === 'OBSERVED') return 'NOTED';
  if (assisted) return `ASSISTED · ${status === 'GIVEN' ? 'TAKEN' : status}`;
  return status;
}

export function medRows(meds: MedPassRow[], now: number): MedRow[] {
  return meds
    .map((m): MedRow => {
      const due = m.dueAt ? Date.parse(m.dueAt) : null;
      const recorded = m.today_status !== null;
      const st = medRecordLabel(m.today_status, m.today_assisted);
      const taken = m.today_status === 'GIVEN';
      const prn = isPrn(m.frequency);
      const level: MedRow['level'] = m.service_level === 'assistance' || m.service_level === 'self_administered' ? m.service_level : 'administer';
      return {
        med: m,
        key: m.medication_id,
        prn,
        level,
        res: m.resident_name ?? 'Resident',
        room: m.room ? `Room ${m.room}` : '',
        name: m.name ?? 'Medication',
        dose: [m.dosage, m.frequency].filter(Boolean).join(' · '),
        due,
        recorded,
        st,
        stStyle:
          'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:4px;padding:3px 8px;color:' +
          (taken ? '#1F6B52' : '#3C454F') + ';background:' + (taken ? 'rgba(47,138,111,.13)' : 'rgba(34,39,46,.13)') + ';',
        dueText: recorded ? '' : level === 'self_administered' ? 'takes it on their own' : prn && due === null ? 'as needed' : due === null ? 'no time on file' : dueFmt(due - now),
        dueColor: level !== 'self_administered' && due !== null && due < now ? '#B3261E' : '#3C454F',
        rowStyle:
          'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:11px 4px;border-top:1px solid rgba(34,39,46,.10);min-width:0;' +
          (recorded ? 'opacity:.6;' : level !== 'self_administered' && due !== null && due < now ? 'box-shadow:inset 3px 0 0 #B3261E;' : ''),
      };
    })
    // Recorded last; as-needed and self-administered orders after the scheduled ones still to give.
    .sort((p, q) => {
      const later = (r: MedRow) => r.prn || r.level === 'self_administered';
      return (p.recorded === q.recorded ? 0 : p.recorded ? 1 : -1) || (later(p) === later(q) ? 0 : later(p) ? 1 : -1) || (p.due ?? Infinity) - (q.due ?? Infinity);
    });
}

// ── Nutrition (design nut*, L36367) ─────────────────────────────────────────

/** Missed Nursing/NTA coding, dollars per patient day: a published industry average. */
export const NUT_PPD = 80;
export const NUT_DOC = [
  'Dietitian assessment within the last 30 days',
  'Weights recorded weekly, same scale, same conditions',
  'Physician-documented diagnosis within the last 60 days',
  'Intake or supplement records that support the finding',
];

export interface NutView {
  row: NutritionRow;
  i: number;
  name: string;
  room: string;
  state: string;
  stateStyle: string;
  wt: string;
  wtSrc: string;
  wtSrcStyle: string;
  obsLine: string;
  obsStyle: string;
  l30: string;
  l180: string;
  chair: string;
  chairSrc: string;
  chairSrcStyle: string;
  chairNote: string;
  chairColor: string;
  trig: string;
  window: string;
  inWindow: boolean;
  dueAt: number | null;
  windowStyle: string;
  glp: string;
  hasGlp: boolean;
  glpNote: string;
  exposure: string;
  docLine: string;
  docStyle: string;
  ardDays: number | null;
}

const LB_PER_KG = 2.20462;

function agoShort(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export function nutView(n: NutritionRow, i: number, now: number): NutView {
  const obs = n.observations ?? [];
  const last = obs.length ? obs[obs.length - 1] : null;
  const measured = !!last;
  const lb = last ? last.lb : n.weight.currentKg != null ? n.weight.currentKg * LB_PER_KG : null;
  const chair = last ? last.chair : n.chairStand ?? null;
  const l30 = n.weight.pct30 != null ? Math.abs(n.weight.pct30) : null;
  const l180 = n.weight.pct180 != null ? Math.abs(n.weight.pct180) : null;
  const wtTrig = n.trigger !== 'function_only' && ((l30 ?? 0) >= 5 || (l180 ?? 0) >= 10);
  const fnTrig = chair != null ? chair < 8 : n.functionDecline;
  const ard = n.ardAt ? Date.parse(n.ardAt) : null;
  const ardDays = ard !== null ? Math.max(0, Math.round((ard - now) / 86_400_000)) : null;
  const window = !!n.partA && ard !== null;
  const docs = n.docs ?? [];
  const docHave = docs.filter(Boolean).length;
  const state = wtTrig && window ? 'CODABLE' : !wtTrig && fnTrig ? 'FUNCTION ONLY' : wtTrig || fnTrig ? 'MONITOR' : 'CLEAR';
  const sc = state === 'CODABLE' ? '#3C454F' : state === 'FUNCTION ONLY' ? '#2F5C97' : state === 'MONITOR' ? '#54606C' : '#1F6B52';
  const sbg = state === 'CODABLE' ? 'rgba(34,39,46,.14)' : state === 'FUNCTION ONLY' ? 'rgba(47,92,151,.11)' : state === 'MONITOR' ? 'rgba(34,39,46,.06)' : 'rgba(47,138,111,.13)';
  const trig = wtTrig && fnTrig ? 'weight and function' : wtTrig ? 'weight only' : fnTrig ? ((l30 ?? 0) >= 2 ? 'function decline; weight loss below the MDS threshold' : 'function decline with no weight loss') : 'neither';
  const src = (m: boolean) =>
    'display:inline-block;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;border-radius:4px;padding:1px 5px;color:' + (m ? '#1F6B52' : '#3C454F') + ';background:' + (m ? 'rgba(47,138,111,.13)' : 'rgba(34,39,46,.14)') + ';';
  const bmi = n.bmi != null && last && n.weight.currentKg ? n.bmi * (last.lb / (n.weight.currentKg * LB_PER_KG)) : n.bmi ?? null;
  return {
    row: n,
    i,
    name: n.name ?? 'Resident',
    room: n.room ? `Room ${n.room}` : '',
    state,
    stateStyle: chip(sc, sbg).replace('padding:2px 7px', 'padding:2px 7px'),
    wt: `${lb != null ? `${Math.round(lb)} lb` : 'No weight on file'}${bmi != null ? ` · BMI ${bmi.toFixed(1)}` : ''}`,
    wtSrc: measured ? 'RECORDED' : 'SAMPLE',
    wtSrcStyle: src(measured),
    obsLine: obs.length === 0 ? 'sample figures · not yet measured by staff' : `${obs.length} recorded observation${obs.length === 1 ? '' : 's'} · last by ${last!.by}, ${agoShort(now - Date.parse(last!.at))}`,
    obsStyle: 'font-size:14px;color:' + (obs.length ? '#1F6B52' : '#3C454F') + ';',
    l30: l30 != null ? `${l30.toFixed(1)}% in 30d` : 'no 30-day weight',
    l180: l180 != null ? `${l180.toFixed(1)}% in 180d` : 'no 180-day weight',
    chair: chair != null ? `${chair} reps in 30s` : 'no chair-stand on file',
    chairSrc: measured ? 'RECORDED' : 'SAMPLE',
    chairSrcStyle: src(measured),
    chairNote: fnTrig ? 'below the 8-rep marker' : 'within range',
    chairColor: fnTrig ? '#3C454F' : '#54606C',
    trig: `triggered by ${trig}`,
    window: window ? `5-day ARD in ${ardDays}${ardDays === 1 ? ' day' : ' days'}` : 'not in an MDS window',
    inWindow: window,
    dueAt: window ? ard : null,
    windowStyle: 'font-size:14px;font-weight:' + (window && (ardDays ?? 9) <= 2 ? '700' : '600') + ';color:' + (window ? ((ardDays ?? 9) <= 2 ? '#B3261E' : '#3C454F') : '#54606C') + ';',
    glp: n.glp1 ?? '',
    hasGlp: !!n.glp1,
    glpNote:
      n.glp1 && n.glp1.indexOf('stopped') > -1
        ? 'Cessation usually brings weight regain, and weight cycling is linked to lower lean mass. Watch strength, not the scale.'
        : 'On treatment, weight loss here is expected. Judge it against strength, not the scale alone.',
    exposure: state === 'CODABLE' ? `$${NUT_PPD}/day at risk · industry average` : 'no coding exposure',
    docLine: `${docHave} of ${NUT_DOC.length} documentation items on file`,
    docStyle: 'font-size:14px;font-weight:600;color:' + (docHave === NUT_DOC.length ? '#1F6B52' : '#3C454F') + ';',
    ardDays,
  };
}

// ── Benefits (design ben*, L37719) ──────────────────────────────────────────

export type BenKind = 'rebate' | 'benefit' | 'recert';
export const kindOfBenefit = (b: BenefitItem): BenKind => (b.consequenceClass === 2 ? 'rebate' : b.consequenceClass === 3 ? 'benefit' : 'recert');
export const daysLeft = (b: BenefitItem, now: number): number | null => {
  if (!b.dueDate) return null;
  const [y, m, d] = b.dueDate.split('-').map(Number);
  const due = new Date(y, m - 1, d).getTime();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today.getTime()) / 86_400_000);
};
export const isHandled = (b: BenefitItem) => b.status === 'complete' || b.status === 'waived';

// ── Referrals (design wRef*, L36300) ────────────────────────────────────────

/** The design's four requirements, over the reporter checklist keys they correspond to. */
export const WCHK = [
  { key: 'document_observations', label: 'The narrative below is in the resident’s own words, or describes what was observed', kind: 'auto' as const, hint: 'satisfied once the narrative is written' },
  { key: 'preserve_evidence', label: 'Physical finding: photograph or measurement attached, or marked not applicable', kind: 'choice' as const },
  { key: 'ensure_safety', label: 'The resident is safe right now - immediate danger ruled out, or 911 called', kind: 'tick' as const },
  { key: 'reporter_attests', label: 'I am the person making this report and this account is mine', kind: 'attest' as const },
];

export interface RefDraft {
  narrative?: string;
  phys?: 'attached' | 'na';
  ticks?: Record<string, boolean>;
}

export function refDone(r: Referral, d: RefDraft | undefined): number {
  const narr = d?.narrative ?? r.narrative ?? '';
  const cl = r.checklist ?? {};
  const phys = d?.phys ?? (cl.preserve_evidence === 'na' || cl.preserve_evidence === 'attached' ? (cl.preserve_evidence as 'na' | 'attached') : cl.preserve_evidence === true ? 'attached' : undefined);
  let n = 0;
  if (narr.trim().length >= 40) n++;
  if (phys) n++;
  if ((d?.ticks?.ensure_safety ?? cl.ensure_safety) === true) n++;
  if ((d?.ticks?.reporter_attests ?? cl.reporter_attests) === true) n++;
  return n;
}

export const isFiled = (r: Referral) => r.status === 'SUBMITTED' || r.status === 'ACKNOWLEDGED' || r.status === 'CLOSED';
